import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
  faUser,
  faPhone,
  faHome,
  faCheckCircle,
  faBell,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

type KioskScreen =
  | 'home'
  | 'guest-reservation'
  | 'guest-passcode'
  | 'guest-confirming'
  | 'apartment-select'
  | 'call-screen';

@Component({
  selector: 'app-kiosk-access',
  templateUrl: './kiosk-access.component.html',
  styleUrls: ['./kiosk-access.component.css'],
  standalone: false
})
export class KioskAccessComponent {
  private readonly confirmationDurationMs = 3000;
  private readonly kioskResetDurationMs = 7000;
  faUser = faUser;
  faPhone = faPhone;
  faHome = faHome;
  faCheckCircle = faCheckCircle;
  faBell = faBell;
  faTimes = faTimes;

  screen: KioskScreen = 'home';
  reservationCode = '';
  passCode = '';
  codeError = '';
  searchText = '';
  showSuccessModal = false;
  selectedApartment: number | null = null;
  callMessage = '';
  private confirmationTimer: ReturnType<typeof setTimeout> | null = null;
  private resetTimer: ReturnType<typeof setTimeout> | null = null;
  private clockTimer: ReturnType<typeof setInterval> | null = null;
  private weatherTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  currentTimeLabel = '';
  currentDateLabel = '';
  weatherLabel = 'Loading weather...';

  apartments: number[] = Array.from({ length: 186 }, (_, index) => index + 1);

  ngOnInit(): void {
    this.updateClock();
    this.clockTimer = setInterval(() => {
      this.updateClock();
    }, 1000);

    this.loadLocalWeather();
    this.weatherTimer = setInterval(() => {
      this.loadLocalWeather();
    }, 900000);
  }

  ngOnDestroy(): void {
    this.clearTimers();

    if (this.clockTimer) {
      clearInterval(this.clockTimer);
      this.clockTimer = null;
    }

    if (this.weatherTimer) {
      clearInterval(this.weatherTimer);
      this.weatherTimer = null;
    }
  }

  get filteredApartments(): number[] {
    const value = (this.searchText || '').trim();
    if (!value) {
      return this.apartments;
    }

    return this.apartments.filter((apartment) => apartment.toString().includes(value));
  }

  openGuest(): void {
    this.clearTimers();
    this.screen = 'guest-reservation';
    this.reservationCode = '';
    this.passCode = '';
    this.codeError = '';
    this.callMessage = '';
  }

  openCall(): void {
    this.clearTimers();
    this.screen = 'apartment-select';
    this.searchText = '';
    this.selectedApartment = null;
    this.callMessage = '';
    this.codeError = '';
  }

  goHome(): void {
    this.clearTimers();
    this.screen = 'home';
    this.reservationCode = '';
    this.passCode = '';
    this.searchText = '';
    this.codeError = '';
    this.selectedApartment = null;
    this.callMessage = '';
    this.showSuccessModal = false;
  }

  sanitizeFourDigits(value: string): string {
    return String(value ?? '').replace(/\D/g, '').slice(0, 4);
  }

  private isTestCodeValid(code: string): boolean {
    return this.sanitizeFourDigits(code) === '1234';
  }

  proceedReservationCode(): void {
    this.reservationCode = this.sanitizeFourDigits(this.reservationCode);

    if (!this.isTestCodeValid(this.reservationCode)) {
      this.codeError = 'Invalid reservation code. Please try again.';
      return;
    }

    this.codeError = '';
    this.screen = 'guest-passcode';
  }

  pressCodeKey(key: string): void {
    if (key === 'clear') {
      this.clearCurrentCode();
      return;
    }

    if (key === 'continue') {
      this.continueCurrentCode();
      return;
    }

    if (!/^[0-9]$/.test(key)) {
      return;
    }

    this.codeError = '';

    if (this.screen === 'guest-reservation') {
      this.reservationCode = this.sanitizeFourDigits(`${this.reservationCode}${key}`);
      return;
    }

    if (this.screen === 'guest-passcode') {
      this.passCode = this.sanitizeFourDigits(`${this.passCode}${key}`);
    }
  }

  private clearCurrentCode(): void {
    this.codeError = '';

    if (this.screen === 'guest-reservation') {
      this.reservationCode = '';
      return;
    }

    if (this.screen === 'guest-passcode') {
      this.passCode = '';
    }
  }

  private continueCurrentCode(): void {
    if (this.screen === 'guest-reservation') {
      this.proceedReservationCode();
      return;
    }

    if (this.screen === 'guest-passcode') {
      this.proceedPassCode();
    }
  }

  proceedPassCode(): void {
    this.passCode = this.sanitizeFourDigits(this.passCode);

    if (!this.isTestCodeValid(this.passCode)) {
      this.codeError = 'Invalid passcode. Please try again.';
      return;
    }

    this.clearTimers();
    this.codeError = '';
    this.screen = 'guest-confirming';
    this.showSuccessModal = false;

    this.confirmationTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.showSuccessModal = true;
        this.cdr.markForCheck();

        this.resetTimer = setTimeout(() => {
          this.ngZone.run(() => {
            this.goHome();
            this.cdr.markForCheck();
          });
        }, this.kioskResetDurationMs);
      });
    }, this.confirmationDurationMs);
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
    this.goHome();
  }

  private clearTimers(): void {
    if (this.confirmationTimer) {
      clearTimeout(this.confirmationTimer);
      this.confirmationTimer = null;
    }

    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
  }

  chooseApartment(apartment: number): void {
    this.selectedApartment = apartment;
    this.callMessage = '';
    this.screen = 'call-screen';
  }

  cancelCall(): void {
    this.callMessage = '';
    this.screen = 'apartment-select';
  }

  callOrNotify(): void {
    this.callMessage = 'Call/Notify sent successfully.';
  }

  private updateClock(): void {
    const now = new Date();
    this.currentTimeLabel = now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });

    this.currentDateLabel = now.toLocaleDateString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  private loadLocalWeather(): void {
    if (!('geolocation' in navigator)) {
      this.weatherLabel = 'Weather unavailable';
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        const endpoint =
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}` +
          `&longitude=${longitude}&current=temperature_2m,weather_code&temperature_unit=celsius`;

        try {
          const response = await fetch(endpoint);
          if (!response.ok) {
            this.weatherLabel = 'Weather unavailable';
            return;
          }

          const data = await response.json();
          const temperature = Math.round(Number(data?.current?.temperature_2m));
          const weatherCode = Number(data?.current?.weather_code);

          if (!Number.isFinite(temperature) || !Number.isFinite(weatherCode)) {
            this.weatherLabel = 'Weather unavailable';
            return;
          }

          this.weatherLabel = `${this.getWeatherText(weatherCode)} ${temperature}C`;
        } catch {
          this.weatherLabel = 'Weather unavailable';
        }
      },
      () => {
        this.weatherLabel = 'Weather unavailable';
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  }

  private getWeatherText(code: number): string {
    if (code === 0) {
      return 'Clear';
    }

    if ([1, 2, 3].includes(code)) {
      return 'Cloudy';
    }

    if ([45, 48].includes(code)) {
      return 'Fog';
    }

    if ([51, 53, 55, 56, 57].includes(code)) {
      return 'Drizzle';
    }

    if ([61, 63, 65, 66, 67].includes(code)) {
      return 'Rain';
    }

    if ([71, 73, 75, 77].includes(code)) {
      return 'Snow';
    }

    if ([80, 81, 82].includes(code)) {
      return 'Rain showers';
    }

    if ([85, 86].includes(code)) {
      return 'Snow showers';
    }

    if ([95, 96, 99].includes(code)) {
      return 'Thunderstorm';
    }

    return 'Weather';
  }
}
